import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';

export interface PlayerKpisMonthlyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
}

export class PlayerKpisMonthlyDataLoaderFactory extends Factory<
    DataLoader,
    PlayerKpisMonthlyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'PlayerKpisMonthlyDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: PlayerKpisMonthlyDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { dateTrunc, splitSelect, splitGroupBy, where } =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,

                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [
                                // `first_open_date <= date_dim.days_ago_30`,
                                // 'day_no BETWEEN 1 AND 30',
                                // 'validation_flag IS NULL',
                                'date >= DATE_TRUNC(DATE_SUB(CURRENT_DATE(), INTERVAL 15 MONTH), MONTH)',
                            ],
                        }
                    );

                const tableFullPath = generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );
                const lastDateQuery = `(SELECT MAX(date) AS last_date FROM \`${tableFullPath}\`)`;
                const lastDateQueryResponse = await DataLoader.runQuery(
                    lastDateQuery,
                    options.queryCacheService
                );
                const lastDate = (lastDateQueryResponse.result[0] as any)
                    .last_date.value;
                const usd_pln =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';

                const query = `
-- DataLoaderId: ${this.instanceId}
-- ZAKTUALIZUJ datę w selectie kolumny avg_DAU (linia 21)

CREATE TEMP FUNCTION
  DaysInMonth(d DATE,
    last_date DATE)
  RETURNS INT64 AS (
  IF
    ( DATE_TRUNC(d, MONTH) = DATE_TRUNC(last_date, MONTH)
      -- if current month
      , EXTRACT(DAY
      FROM
        last_date)
      -- if not current month
      , EXTRACT(DAY
      FROM
        LAST_DAY(d)) ) );

SELECT *,
  sum_of_DAU / (DaysInMonth(date, '${lastDate}')) AS avg_DAU,
FROM (
  SELECT
    ${splitSelect}DATE_TRUNC(date, MONTH) AS date
    , SUM(dau) AS sum_of_DAU
    , SUM(mau) AS MAU
    , SUM(new_users) AS new_users

    , SUM(revenue_net_${usd_pln}) AS revenue_net_${usd_pln}
    , SUM(revenue_gross_${usd_pln}) AS revenue_gross_${usd_pln}
    , SUM(inapp_revenue_net_${usd_pln}) AS inapp_revenue_net_${usd_pln}
    , SUM(inapp_revenue_gross_${usd_pln}) AS inapp_revenue_gross_${usd_pln}
    , SUM(ad_revenue_net_${usd_pln}) AS ad_revenue_net_${usd_pln}
    , SUM(ad_revenue_gross_${usd_pln}) AS ad_revenue_gross_${usd_pln}
    
    , SUM(revenue_net_${usd_pln})/SUM(dau) AS ARPDAU_net_${usd_pln}
    , SUM(revenue_gross_${usd_pln})/SUM(dau) AS ARPDAU_gross_${usd_pln}
    , SUM(inapp_revenue_net_${usd_pln})/SUM(dau) AS inapp_ARPDAU_net_${usd_pln}
    , SUM(inapp_revenue_gross_${usd_pln})/SUM(dau) AS inapp_ARPDAU_gross_${usd_pln}
    , SUM(ad_revenue_net_${usd_pln})/SUM(dau) AS ad_ARPDAU_net_${usd_pln}
    , SUM(ad_revenue_gross_${usd_pln})/SUM(dau) AS ad_ARPDAU_gross_${usd_pln}
    
    , SUM(daily_payers_count)/SUM(dau) AS conversion_rate

    , SUM(monthly_cohort_kpis.revenue_lte_d30_maturity_d30_net_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS ARPU_d30_net_${usd_pln}
    , SUM(monthly_cohort_kpis.revenue_lte_d30_maturity_d30_gross_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS ARPU_d30_gross_${usd_pln}

    , SUM(monthly_cohort_kpis.inapp_revenue_lte_d30_maturity_d30_net_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS inapp_ARPU_d30_net_${usd_pln}
    , SUM(monthly_cohort_kpis.inapp_revenue_lte_d30_maturity_d30_gross_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS inapp_ARPU_d30_gross_${usd_pln}

    , SUM(monthly_cohort_kpis.ad_revenue_lte_d30_maturity_d30_net_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS ad_ARPU_d30_net_${usd_pln}
    , SUM(monthly_cohort_kpis.ad_revenue_lte_d30_maturity_d30_gross_${usd_pln})/SUM(monthly_cohort_kpis.initial_cohort_d30) AS ad_ARPU_d30_gross_${usd_pln}
  FROM
    \`${tableFullPath}\`
  ${where}
  GROUP BY ${splitGroupBy}date
  )
ORDER BY ${splitGroupBy}date asc
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
